import { CommonModule } from '@angular/common';
import { Component, Inject, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AuthService } from '@app/services/auth.service';
import { ChargerClaim, ClaimRole } from '@app/services/charger-claim.interface';
import { ClaimService } from '@app/services/claim.service';
import { catchError, finalize } from 'rxjs';
import { SimplebarAngularModule } from 'simplebar-angular';
import { AlertComponent } from '../alert/alert.component';

export interface ClaimChargerDialogData {
  stationId: number;
  chargerId?: number;
  stationName?: string;
}

/** Labels for the role select, ordered as shown. */
const ROLE_LABELS: Record<ClaimRole, string> = {
  [ClaimRole.OWNER]: 'Owner',
  [ClaimRole.OPERATOR]: 'Operator',
  [ClaimRole.MANAGER]: 'Manager',
};

@Component({
  selector: 'app-claim-charger-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    SimplebarAngularModule,
    AlertComponent,
  ],
  templateUrl: './claim-charger-dialog.component.html',
  styleUrl: './claim-charger-dialog.component.scss',
})
export class ClaimChargerDialogComponent {
  dialogRef = inject(MatDialogRef<ClaimChargerDialogComponent>);
  private _formBuilder = inject(FormBuilder);

  step = signal<'form' | 'otp' | 'done'>('form');
  isLoading = signal(false);
  claim = signal<ChargerClaim | null>(null);

  ROLE_LABELS = ROLE_LABELS;
  keepOriginalOrder = () => 0;

  successMessage = '';
  errorMessage = '';

  claimForm = this._formBuilder.group({
    role: [ClaimRole.OWNER, Validators.required],
    contactEmail: ['', [Validators.required, Validators.email]],
    contactPhone: [''],
    evidenceNote: [''],
  });

  otpForm = this._formBuilder.group({
    code: ['', [Validators.required, Validators.minLength(6)]],
  });

  constructor(
    private claimService: ClaimService,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public props: ClaimChargerDialogData,
  ) {
    // Claims are login-only; close if the session drops.
    this.authService.whenNotAuthenticated(() => {
      this.dialogRef.close();
    });
  }

  get contactEmail(): string {
    return this.claimForm.value.contactEmail || '';
  }

  get isVerified(): boolean {
    return this.claim()?.verificationStatus === 'VERIFIED';
  }

  close() {
    this.dialogRef.close(this.claim());
  }

  submitClaim() {
    if (!this.claimForm.valid) return;

    const claimantUserId = this.authService.userSignal()?.uid;
    if (!claimantUserId) {
      this.dialogRef.close();
      return;
    }

    const value = this.claimForm.value;
    this.isLoading.set(true);
    this.errorMessage = '';

    this.claimService
      .submitClaim({
        stationId: this.props.stationId,
        chargerId: this.props.chargerId,
        claimantUserId,
        role: value.role as ClaimRole,
        contactEmail: value.contactEmail as string,
        contactPhone: value.contactPhone || undefined,
        evidenceNote: value.evidenceNote || undefined,
      })
      .pipe(
        catchError(() => {
          this.errorMessage = 'Could not start the claim. Try again.';
          throw new Error('claim failed');
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe((claim) => {
        this.claim.set(claim);
        this.step.set('otp');
      });
  }

  verifyOtp() {
    const claim = this.claim();
    if (!this.otpForm.valid || !claim) return;

    this.isLoading.set(true);
    this.errorMessage = '';

    this.claimService
      .verifyOtp({
        claimId: claim.id,
        code: this.otpForm.value.code as string,
      })
      .pipe(
        catchError(() => {
          this.errorMessage = 'That code did not match. Try again.';
          throw new Error('otp failed');
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe((updated) => {
        this.claim.set(updated);
        this.step.set('done');
      });
  }

  resendOtp() {
    const claim = this.claim();
    if (!claim) return;

    this.claimService.resendOtp(claim.id).subscribe(() => {
      this.successMessage = 'Code re-sent';
    });
  }

  backToForm() {
    this.successMessage = '';
    this.errorMessage = '';
    this.step.set('form');
  }
}
